import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { safeAboutUrl, sanitizeAboutHtml } from "../../../../../utils/aboutContent";
import { formControlClass, btnSecondaryClass } from "../../../dashboardStyles";

// The lead paragraph (`ep-about-lead`) is a class on a paragraph; without this
// TipTap would drop it the first time the text is edited.
const KeepClasses = Extension.create({
  name: "aboutKeepClasses",
  addGlobalAttributes: () => [{ types: ["paragraph", "heading"], attributes: { class: { default: null } } }],
});

const toolButtonClass = (active) =>
  `inline-flex size-11 cursor-pointer items-center justify-center rounded-md text-base transition focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-50 ${
    active ? "bg-brand-50 text-brand-700" : "text-ink-700 hover:bg-ink-100"
  }`;

/** An empty editor's HTML is "<p></p>"; stored, it is nothing. */
const cleanHtml = (html) => (html === "<p></p>" ? "" : html);

/**
 * A short formatted text — a block's description — edited as text, with the
 * few marks the About section uses: bold, italic, underline, lists and links,
 * and with `headings` also titles, a quote and a rule.
 *
 * The output is HTML, sanitized again when the About section is written out.
 */
const RichTextField = ({ id, label, value = "", onChange, headings = false, disabled = false }) => {
  const { t } = useTranslation();
  const labelId = useId();
  const lastEmitted = useRef(value);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: headings ? { levels: [2, 3] } : false,
        blockquote: headings ? {} : false,
        horizontalRule: headings ? {} : false,
        code: false,
        codeBlock: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
      }),
      KeepClasses,
    ],
    content: value,
    editable: !disabled,
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        id,
        role: "textbox",
        "aria-multiline": "true",
        "aria-labelledby": labelId,
        class:
          "min-h-28 px-3 py-2.5 text-sm leading-relaxed text-ink-800 focus:outline-none [overflow-wrap:anywhere] [&_a]:text-brand-700 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-ink-200 [&_blockquote]:pl-3 [&_blockquote]:italic [&_h2]:my-2 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:my-2 [&_h3]:font-semibold [&_hr]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5",
      },
      transformPastedHTML: (html) => sanitizeAboutHtml(html),
    },
    onUpdate: ({ editor: current }) => {
      const html = cleanHtml(current.getHTML());
      lastEmitted.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  // A value from elsewhere (HTML applied, another block selected) replaces the text.
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    editor.commands.setContent(value || "", { emitUpdate: false });
    lastEmitted.current = value;
  }, [editor, value]);

  const run = (command) => () => command(editor.chain().focus()).run();
  const tools = [
    ["bold", "bi-type-bold", (chain) => chain.toggleBold(), "bold"],
    ["italic", "bi-type-italic", (chain) => chain.toggleItalic(), "italic"],
    ["underline", "bi-type-underline", (chain) => chain.toggleUnderline(), "underline"],
    ["bulletList", "bi-list-ul", (chain) => chain.toggleBulletList(), "bulletList"],
    ["orderedList", "bi-list-ol", (chain) => chain.toggleOrderedList(), "orderedList"],
    ...(headings ? [["blockquote", "bi-quote", (chain) => chain.toggleBlockquote(), "blockquote"]] : []),
  ];

  const applyLink = () => {
    const href = safeAboutUrl(linkUrl.trim());
    if (!href) {
      setLinkError(true);
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkOpen(false);
    setLinkUrl("");
    setLinkError(false);
  };

  const style = editor?.isActive("heading", { level: 2 })
    ? "h2"
    : editor?.isActive("heading", { level: 3 })
      ? "h3"
      : "p";

  return (
    <div className="min-w-0">
      <span id={labelId} className="mb-1.5 block text-sm font-semibold text-ink-800">
        {label}
      </span>
      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white transition focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/20">
        <div
          role="toolbar"
          aria-label={t("Dashboard.AboutEditor.formatting")}
          className="flex flex-wrap items-center gap-0.5 border-b border-ink-200 bg-ink-50 px-1 py-0.5"
        >
          {headings && (
            <select
              aria-label={t("Dashboard.AboutEditor.textStyle")}
              disabled={disabled || !editor}
              value={style}
              onChange={(event) =>
                event.target.value === "p"
                  ? editor.chain().focus().setParagraph().run()
                  : editor.chain().focus().toggleHeading({ level: Number(event.target.value.slice(1)) }).run()
              }
              className="mr-1 min-h-11 cursor-pointer rounded-md border-0 bg-transparent px-2 text-sm text-ink-700 focus-visible:outline-2 focus-visible:outline-brand-600"
            >
              <option value="p">{t("Dashboard.AboutEditor.paragraph")}</option>
              <option value="h2">{t("Dashboard.AboutEditor.heading")}</option>
              <option value="h3">{t("Dashboard.AboutEditor.subheading")}</option>
            </select>
          )}
          {tools.map(([key, icon, command, mark]) => (
            <button
              key={key}
              type="button"
              title={t(`Dashboard.AboutEditor.${key}`)}
              aria-label={t(`Dashboard.AboutEditor.${key}`)}
              aria-pressed={Boolean(editor?.isActive(mark))}
              disabled={disabled || !editor}
              onClick={run(command)}
              className={toolButtonClass(Boolean(editor?.isActive(mark)))}
            >
              <i className={`bi ${icon}`} aria-hidden="true" />
            </button>
          ))}
          <button
            type="button"
            title={t("Dashboard.AboutEditor.link")}
            aria-label={t("Dashboard.AboutEditor.link")}
            aria-expanded={linkOpen}
            aria-pressed={Boolean(editor?.isActive("link"))}
            disabled={disabled || !editor}
            onClick={() => {
              setLinkUrl(editor.getAttributes("link").href || "");
              setLinkError(false);
              setLinkOpen((open) => !open);
            }}
            className={toolButtonClass(linkOpen || Boolean(editor?.isActive("link")))}
          >
            <i className="bi bi-link-45deg text-lg" aria-hidden="true" />
          </button>
        </div>
        {linkOpen && (
          <div className="flex flex-col gap-2 border-b border-ink-200 bg-ink-50 px-3 py-2.5 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <input
                type="url"
                value={linkUrl}
                placeholder="https://"
                aria-label={t("Dashboard.AboutEditor.linkUrl")}
                aria-invalid={linkError}
                onChange={(event) => setLinkUrl(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    applyLink();
                  }
                }}
                className={formControlClass}
              />
              {linkError && (
                <p className="mt-1 mb-0 text-xs text-red-600" role="alert">
                  {t("Dashboard.AboutEditor.invalidUrl")}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" className={btnSecondaryClass} onClick={applyLink}>
                {t("Dashboard.AboutEditor.insertLink")}
              </button>
              {editor?.isActive("link") && (
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={() => {
                    editor.chain().focus().extendMarkRange("link").unsetLink().run();
                    setLinkOpen(false);
                  }}
                >
                  {t("Dashboard.AboutEditor.unlink")}
                </button>
              )}
            </div>
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

RichTextField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  /** Titles, quotes and rules too: for a Text block's whole content. */
  headings: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default RichTextField;
